<template>
  <v-dialog
    v-model="show"
    max-width="600px"
    persistent
    :disabled="loading"
    scrollable
  >
    <v-card>
      <v-card-title class="headline primary white--text py-3 d-flex align-center">
        <v-icon left color="white">mdi-folder-zip-outline</v-icon>
        <span>Cập nhật từ file Zip (GDB)</span>
        <v-spacer></v-spacer>
        <v-btn icon dark small @click="close" :disabled="loading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4 pb-2">
        <v-alert
          type="info"
          dense
          text
          class="mb-4"
          icon="mdi-information-outline"
        >
          <div class="font-weight-medium mb-1">Hướng dẫn cập nhật:</div>
          <ul class="pl-4 text-body-2 mb-0">
            <li>Tải lên tệp tin nén <strong>.zip</strong> chứa thư mục File Geodatabase (<strong>.gdb</strong>).</li>
            <li>Hệ thống sẽ giải nén và đồng bộ dữ liệu ranh giới vào bảng Tỉnh/Thành phố.</li>
            <li>Lớp dữ liệu mặc định trong GDB: <strong>DiaPhanHanhChinhTinh</strong>.</li>
          </ul>
        </v-alert>

        <v-form ref="form" v-model="valid" lazy-validation>
          <v-file-input
            v-model="file"
            label="Chọn tệp tin .zip chứa thư mục .gdb *"
            accept=".zip,application/zip,application/x-zip-compressed"
            prepend-icon="mdi-file-upload-outline"
            outlined
            dense
            show-size
            truncate-length="25"
            :rules="fileRules"
            :disabled="loading"
            @change="onFileChange"
            placeholder="Nhấp để chọn tệp .zip từ máy tính"
            class="mb-2"
          ></v-file-input>

          <v-text-field
            v-model="layerName"
            label="Tên lớp trong GDB (tùy chọn)"
            placeholder="DiaPhanHanhChinhTinh"
            hint="Mặc định là DiaPhanHanhChinhTinh"
            persistent-hint
            outlined
            dense
            :disabled="loading"
            prepend-icon="mdi-layers-outline"
            class="mb-3"
          ></v-text-field>
        </v-form>

        <!-- Progress bar while uploading/importing -->
        <div v-if="loading" class="mt-2 mb-3">
          <div class="d-flex justify-space-between mb-1">
            <span class="text-caption font-weight-medium">
              <v-icon x-small color="primary" class="mdi-spin mr-1">mdi-loading</v-icon>
              {{ progressStatusText }}
            </span>
            <span class="text-caption font-weight-bold primary--text">
              {{ uploadProgress }}%
            </span>
          </div>
          <v-progress-linear
            :value="uploadProgress"
            :indeterminate="uploadProgress === 100"
            color="primary"
            height="10"
            rounded
            striped
          ></v-progress-linear>
        </div>

        <!-- Error notification -->
        <v-alert
          v-if="errorMessage"
          type="error"
          dense
          outlined
          class="mt-3 mb-0"
        >
          <div class="font-weight-medium">{{ errorMessage }}</div>
          <div v-if="errorOutputs && errorOutputs.length" class="mt-2 text-caption font-italic">
            <div v-for="(out, idx) in errorOutputs" :key="idx">{{ out }}</div>
          </div>
        </v-alert>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="px-4 py-3">
        <v-spacer></v-spacer>
        <v-btn
          text
          @click="close"
          :disabled="loading"
        >
          Huỷ bỏ
        </v-btn>
        <v-btn
          color="primary"
          depressed
          @click="submit"
          :loading="loading"
          :disabled="!file || loading"
        >
          <v-icon left>mdi-cloud-upload</v-icon>
          Cập nhật dữ liệu
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { API_PROVINCE } from '@api'

export default {
  name: 'ProvinceDialogImport',
  data() {
    return {
      show: false,
      valid: true,
      file: null,
      layerName: 'DiaPhanHanhChinhTinh',
      loading: false,
      uploadProgress: 0,
      errorMessage: '',
      errorOutputs: [],
      fileRules: [
        (v) => !!v || 'Vui lòng chọn tệp tin .zip',
        (v) => {
          if (!v) return true
          const fileName = v.name || ''
          return (
            fileName.toLowerCase().endsWith('.zip') ||
            'Tệp tin phải có định dạng .zip'
          )
        },
      ],
    }
  },
  computed: {
    progressStatusText() {
      if (this.uploadProgress < 100) {
        return `Đang tải tệp tin lên máy chủ (${this.uploadProgress}%)...`
      }
      return 'Đang giải nén và xử lý import GDB vào cơ sở dữ liệu...'
    },
  },
  methods: {
    open() {
      this.resetForm()
      this.show = true
    },
    close() {
      if (this.loading) return
      this.show = false
      this.resetForm()
    },
    resetForm() {
      this.file = null
      this.layerName = 'DiaPhanHanhChinhTinh'
      this.loading = false
      this.uploadProgress = 0
      this.errorMessage = ''
      this.errorOutputs = []
      this.valid = true
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    onFileChange(file) {
      this.errorMessage = ''
      this.errorOutputs = []
      if (!file) return

      const fileName = file.name || ''
      if (!fileName.toLowerCase().endsWith('.zip')) {
        this.errorMessage = 'Định dạng file không hợp lệ. Vui lòng chọn tệp tin có đuôi .zip'
      }
    },
    async submit() {
      if (!this.$refs.form.validate() || !this.file) {
        return
      }

      this.loading = true
      this.uploadProgress = 0
      this.errorMessage = ''
      this.errorOutputs = []

      const formData = new FormData()
      formData.append('file', this.file)
      if (this.layerName && this.layerName.trim()) {
        formData.append('layer_name', this.layerName.trim())
      }

      try {
        const res = await API_PROVINCE.importGdb(formData, (progressEvent) => {
          if (progressEvent && progressEvent.total) {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            this.uploadProgress = Math.min(percent, 100)
          }
        })

        const resData = res || {}
        const successMessage =
          resData.message || 'Import dữ liệu Tỉnh/Thành phố từ GDB thành công!'

        this.$store.dispatch('notify/success', {
          message: successMessage,
        })

        this.show = false
        this.resetForm()
        this.$emit('success')
      } catch (error) {
        console.error('Lỗi khi import GDB:', error)
        let errorMsg = 'Có lỗi xảy ra trong quá trình import dữ liệu.'
        let outputs = []

        if (error.response && error.response.data) {
          const data = error.response.data
          if (data.message) {
            errorMsg = data.message
          }
          if (data.output && Array.isArray(data.output)) {
            outputs = data.output
          }
        } else if (error.message) {
          errorMsg = error.message
        }

        this.errorMessage = errorMsg
        this.errorOutputs = outputs

        this.$store.dispatch('notify/error', {
          message: errorMsg,
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.headline {
  font-size: 1.1rem !important;
  font-weight: 600;
}
</style>
